// src/pages/GenerationPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import { requiresPayment, getPaymentStatusMessage } from '../utils/paymentUtils';
import PaymentRequiredModal from '../components/PaymentRequiredModal';

const GenerationPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('datasets');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handleUploadDocuments = () => {
    // Check if user has active payment before allowing document creation
    if (requiresPayment(user)) {
      setShowPaymentModal(true);
      return;
    }
    
    navigate('/new-document');
  };

  // Fetch datasets
  const fetchDatasets = async () => {
    try {
      const response = await fetch(`${API_URL}/generation/datasets`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDatasets(data.datasets || []);
      } else {
        console.error('Failed to fetch datasets');
      }
    } catch (error) {
      console.error('Error fetching datasets:', error);
    }
  };

  // Fetch available documents
  const fetchAvailableDocuments = async () => {
    try {
      const response = await fetch(`${API_URL}/generation/documents?limit=50`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableDocuments(data.documents || []);
      } else {
        console.error('Failed to fetch documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDatasets(), fetchAvailableDocuments()]);
      setLoading(false);
    };

    if (user) {
      loadData();
    }
  }, [user]);

  const handleCreateDataset = async (formData) => {
    try {
      const response = await fetch(`${API_URL}/generation/datasets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Training dataset created successfully!');
        await fetchDatasets();
        setShowCreateModal(false);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create dataset');
      }
    } catch (error) {
      console.error('Error creating dataset:', error);
      toast.error('Failed to create dataset');
    }
  };

  const handleProcessDataset = async (datasetId) => {
    try {
      const response = await fetch(`${API_URL}/generation/datasets/${datasetId}/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Dataset processing started! Check back for updates.');
        await fetchDatasets();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to start processing');
      }
    } catch (error) {
      console.error('Error processing dataset:', error);
      toast.error('Failed to process dataset');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      processing: 'bg-yellow-100 text-yellow-800',
      ready: 'bg-green-100 text-green-800',
      training: 'bg-blue-100 text-blue-800',
      completed: 'bg-purple-100 text-purple-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900">
        <NavBar />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <NavBar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">AI Training Data Generation</h1>
          <p className="text-gray-400">
            Configure training datasets from your contracts with automatic anonymization to remove personal information.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setSelectedTab('datasets')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'datasets'
                  ? 'border-blue-500 text-blue-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Training Datasets
            </button>
            <button
              onClick={() => setSelectedTab('documents')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                selectedTab === 'documents'
                  ? 'border-blue-500 text-blue-500'
                  : 'border-transparent text-gray-400 hover:text-gray-300'
              }`}
            >
              Available Documents
            </button>
          </nav>
        </div>

        {/* Datasets Tab */}
        {selectedTab === 'datasets' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">Training Datasets</h2>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Create Dataset
              </button>
            </div>

            {datasets.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-8 text-center">
                <h3 className="text-lg font-medium text-white mb-2">No training datasets yet</h3>
                <p className="text-gray-400 mb-4">
                  Create your first training dataset to get started with AI generation.
                </p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Create Your First Dataset
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {datasets.map((dataset) => (
                  <div key={dataset.id} className="bg-gray-800 rounded-lg p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-white mb-1">{dataset.name}</h3>
                        <p className="text-gray-400 text-sm">{dataset.description}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(dataset.status)}`}>
                        {dataset.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-gray-400 text-xs">Type</p>
                        <p className="text-white text-sm">{dataset.datasetType.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Documents</p>
                        <p className="text-white text-sm">{dataset.documentCount || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Tokens</p>
                        <p className="text-white text-sm">{dataset.totalTokens || 0}</p>
                      </div>
                      <div>
                        <p className="text-gray-400 text-xs">Created</p>
                        <p className="text-white text-sm">
                          {new Date(dataset.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => setSelectedDataset(dataset)}
                        className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                      >
                        View Details
                      </button>
                      {dataset.status === 'draft' && dataset.documentCount > 0 && (
                        <button
                          onClick={() => handleProcessDataset(dataset.id)}
                          className="text-green-400 hover:text-green-300 text-sm font-medium"
                        >
                          Start Processing
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {selectedTab === 'documents' && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-white mb-2">Available Documents</h2>
              <p className="text-gray-400 text-sm">
                Select from your uploaded contracts to include in training datasets.
              </p>
            </div>

            {availableDocuments.length === 0 ? (
              <div className="bg-gray-800 rounded-lg p-8 text-center">
                <h3 className="text-lg font-medium text-white mb-2">No documents available</h3>
                <p className="text-gray-400 mb-4">
                  Upload some contracts first to create training datasets.
                </p>
                <button
                  onClick={handleUploadDocuments}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
                >
                  Upload Documents
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {availableDocuments.map((doc) => (
                  <div key={doc.id} className="bg-gray-800 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-white font-medium">{doc.name}</h3>
                        <p className="text-gray-400 text-sm">
                          {doc.fileType} • {Math.round(doc.fileSize / 1024)} KB
                          {doc.hasExtractedText && ` • ${Math.round(doc.textLength / 1000)}k characters`}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {doc.suitableForTraining ? (
                          <span className="text-green-400 text-sm">✓ Suitable for training</span>
                        ) : (
                          <span className="text-yellow-400 text-sm">⚠ May need processing</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Create Dataset Modal */}
        {showCreateModal && (
          <CreateDatasetModal
            onClose={() => setShowCreateModal(false)}
            onSubmit={handleCreateDataset}
          />
        )}

        {/* Dataset Details Modal */}
        {selectedDataset && (
          <DatasetDetailsModal
            dataset={selectedDataset}
            onClose={() => setSelectedDataset(null)}
            onRefresh={fetchDatasets}
          />
        )}

        {/* Payment Required Modal */}
        {showPaymentModal && (
          <PaymentRequiredModal 
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            message={getPaymentStatusMessage(user).message}
            redirectPath={getPaymentStatusMessage(user).redirectPath}
            isFreeTrialMessage={getPaymentStatusMessage(user).isFreeTrialMessage}
          />
        )}
      </div>
    </div>
  );
};

// Create Dataset Modal Component
const CreateDatasetModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    datasetType: 'contract_generation',
    anonymizationConfig: {
      removePII: true,
      preserveStructure: true,
      anonymizationLevel: 'medium'
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium text-white mb-4">Create Training Dataset</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Dataset Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="2"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Dataset Type
            </label>
            <select
              value={formData.datasetType}
              onChange={(e) => setFormData({ ...formData, datasetType: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="contract_generation">Contract Generation</option>
              <option value="clause_analysis">Clause Analysis</option>
              <option value="document_review">Document Review</option>
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Anonymization Level
            </label>
            <select
              value={formData.anonymizationConfig.anonymizationLevel}
              onChange={(e) => setFormData({
                ...formData,
                anonymizationConfig: {
                  ...formData.anonymizationConfig,
                  anonymizationLevel: e.target.value
                }
              })}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="low">Low (Preserve some structure)</option>
              <option value="medium">Medium (Standard anonymization)</option>
              <option value="high">High (Complete redaction)</option>
            </select>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Dataset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Dataset Details Modal Component
const DatasetDetailsModal = ({ dataset, onClose, onRefresh }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-white">{dataset.name}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            ×
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Status</h4>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              dataset.status === 'ready' ? 'bg-green-100 text-green-800' :
              dataset.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {dataset.status}
            </span>
          </div>

          {dataset.description && (
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Description</h4>
              <p className="text-gray-400">{dataset.description}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Type</h4>
              <p className="text-white">{dataset.datasetType.replace('_', ' ')}</p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Documents</h4>
              <p className="text-white">{dataset.documentCount || 0}</p>
            </div>
          </div>

          {dataset.piiDetectionResults && (
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Anonymization Results</h4>
              <div className="bg-gray-700 rounded-lg p-3">
                <p className="text-sm text-gray-300">
                  Processed {dataset.piiDetectionResults.length} documents
                </p>
                {/* Add more detailed results here */}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerationPage;